import { Component, Input } from '@angular/core';
import { CustomerTag } from '../../../../../../models/customer';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { batchAll, CallbackResult, CallbackResultStatus, searchStringList } from '../../../../../../utils';
import { CustomerService } from '../../../../../../services/customer/customer.service';

@Component({
  selector: 'app-bulk-add-or-remove-customer-tag-modal',
  templateUrl: './bulk-add-or-remove-customer-tag-modal.component.html',
  styleUrls: ['./bulk-add-or-remove-customer-tag-modal.component.css']
})
export class BulkAddOrRemoveCustomerTagModalComponent {

  @Input() customerIds: number[];
  @Input() tags: CustomerTag[];
  @Input() isAdd: boolean;
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public tagSearchInput = '';
  public tagName = '';
  public creatingNewTag = false;

  public errorMessage = '';
  public serverError: number = null;

  public loadingSave = false;
  public numberSaved = 0;

  constructor(
    public activeModal: NgbActiveModal,
    public customerService: CustomerService
  ) {}

  public toggleCreatingNewTag(creatingNewTagStatus: boolean) {
    if (this.creatingNewTag === creatingNewTagStatus) { return; }

    this.creatingNewTag = creatingNewTagStatus;
    this.tagSearchInput = '';
    this.tagName = '';
  }

  public getMatchedTags(): string[] {
    return searchStringList(
      this.tagSearchInput,
      this.tags.map(tag => tag.name)
    );
  }

  public handleSubmit() {
    if (this.tagName.trim() === '') { return; }

    this.errorMessage = '';
    this.loadingSave = true;

    this.numberSaved = 0;
    let successfulCount = 0;
    let superfluousCount = 0;

    const customerIdHandler = (customerId: number): Promise<void> => {
      // Stop early if an error message or server error is produced.
      if ((this.errorMessage !== '') || (this.serverError !== null)) {
        return Promise.resolve();
      }

      let savePromise: Promise<void>;
      if (this.isAdd) {
        savePromise = this.customerService.addCustomerTag(customerId, this.tagName);
      } else {
        savePromise = this.customerService.removeCustomerTag(customerId, this.tagName);
      }

      return savePromise
        .then(() => { successfulCount++; })
        .catch(error => {
          if (error.status === 400) {
            superfluousCount++;
          } else if (error.status === 403) {
            this.errorMessage = 'You do not have permission to perform this operation.';
          } else if (error.status >= 500) {
            this.serverError = error.status;
          }
        })
        .finally(() => { this.numberSaved++; });
    };

    batchAll(
      4,
      this.customerIds,
      customerIdHandler
    )
      .then(() => {
        if (this.errorMessage !== '') { return; }

        let successMessage = '';
        if (successfulCount > 0) {
          successMessage = 'Tag \'' + this.tagName + '\' successfully '
            + (this.isAdd ? 'added to ' : 'removed from ')
            + successfulCount.toString() + ' customer'
            + (successfulCount > 1 ? 's' : '') + '.';
        }
        if (superfluousCount > 0) {
          successMessage += ' ' + superfluousCount.toString() + ' customer'
            + (superfluousCount > 1 ? 's' : '')
            + (this.isAdd ? ' already had the tag.' : ' already did not have the tag.');
        }

        this.savedCallback({
          text: successMessage.trim(),
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .finally(() => { this.loadingSave = false; });
  }

  public saveButtonEnabled() {
    return (
      (this.tagName.trim() !== '') &&
      !this.loadingSave
    );
  }

  public getLoadingProgressPercentageStr() {
    return Math.ceil((this.numberSaved / this.customerIds.length) * 100).toString();
  }

}
